/**
 * NOS-117: Hook for rewriting section content with AI.
 * Encapsulates edge function call, loading/error state, and undo capability.
 */
import { useState, useCallback, useRef, useEffect } from "react"
import { toast } from "sonner"
import { supabase } from "@/lib/supabase"
import { useCustomizationStore } from "@/stores/customizationStore"
import { FunctionsHttpError } from "@supabase/supabase-js"

const UNDO_WINDOW_MS = 10_000

export interface UseRewriteSectionResult {
  rewrite: (instruction?: string) => Promise<void>
  isLoading: boolean
  error: string | null
  undo: () => void
  canUndo: boolean
}

export function useRewriteSection(sectionId: string | null, orgId: string | null): UseRewriteSectionResult {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [canUndo, setCanUndo] = useState(false)
  const previousContentRef = useRef<Record<string, unknown> | null>(null)
  const undoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const updateSection = useCustomizationStore((s) => s.updateSection)

  const section = useCustomizationStore((s) =>
    sectionId ? s.sections.find((sec) => sec.id === sectionId) : null
  )

  const clearUndo = useCallback(() => {
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current)
      undoTimeoutRef.current = null
    }
    setCanUndo(false)
    previousContentRef.current = null
  }, [])

  useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current)
    }
  }, [])

  const undo = useCallback(() => {
    const prev = previousContentRef.current
    if (!prev || !sectionId) return
    updateSection(sectionId, prev)
    clearUndo()
  }, [sectionId, updateSection, clearUndo])

  const rewrite = useCallback(
    async (instruction?: string) => {
      if (!sectionId || !orgId || !section) return
      setError(null)
      setIsLoading(true)
      const contentBefore = (section.content as Record<string, unknown>) ?? {}

      try {
        const { data, error: fnError } = await supabase.functions.invoke("rewrite-section", {
          body: { section_id: sectionId, org_id: orgId, instruction: instruction?.trim() || undefined },
        })

        if (fnError) {
          let message = fnError.message ?? "Failed to rewrite section"
          if (fnError instanceof FunctionsHttpError && fnError.context) {
            try {
              const body = (await (fnError.context as Response).json()) as { error?: string }
              if (typeof body?.error === "string") message = body.error
            } catch {
              // keep default message
            }
          }
          setError(message)
          return
        }

        const rewritten = (data as { content?: Record<string, unknown> })?.content
        if (!rewritten || typeof rewritten !== "object") {
          setError("AI returned invalid content")
          return
        }

        previousContentRef.current = contentBefore
        await updateSection(sectionId, rewritten)
        setCanUndo(true)
        undoTimeoutRef.current = setTimeout(clearUndo, UNDO_WINDOW_MS)
        toast.success("Section rewritten")
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err))
      } finally {
        setIsLoading(false)
      }
    },
    [sectionId, orgId, section, updateSection, clearUndo]
  )

  return { rewrite, isLoading, error, undo, canUndo }
}
